import { NameMarkersData } from 'consts/markers';
import Attribute from 'types/Attribute';
import Picture from 'types/Picture';
import ShopConfig from 'types/ShopConfig';
import { ProductCategoryLevels } from 'types/Variant';
import { Filter, FilterValue, QueryFilter } from 'types/Filter';
import OptionGroup, { OptionStatus, Option } from 'types/ProductOptionGroup';
import { NameSchemaType } from 'types/models/NameSchemaType';
import { ProductAttributeValue } from 'types/models/ProductAttributeValue';
import { VariantAttributeValue } from 'types/models/VariantAttributeValue';
import { AttributeGroup } from 'types/models/AttributeGroup';
import { ProductAttributeGroup } from 'types/models/ProductAttributeGroup';
import { SimpleAttributeValue } from 'types/models/SimpleAttributeValue';
import { Product } from 'types/models/Product';
import { Variant } from 'types/models/Variant';
import { Media } from 'types/models/Media';
import { Name } from 'types/models/Name';
import { PackageContent } from 'types/models/PackageContent';
import { VariantPackageContent } from 'types/models/VariantPackageContent';
import { PriceImpact } from 'types/Price';
import { MessageDescriptor, IntlShape } from 'utils/intl';
import getNameByType from 'utils/getNameByType';
import getUniqueFlatArray from 'utils/getUniqueFlatArray';
import confidentFind from 'utils/confidentFind';
import keysOf from 'utils/keysOf';
import map from 'utils/map';
import replaceNameMarkers from 'utils/replaceNameMarkers';
import checkIfAttributeValueMatch from 'utils/checkIfAttributeValueMatch';

const variantSelectorPromotionAlgorithms = ['with-promotion', 'with-promotion-and-filtering'];

const defaultMapping: {
  value: keyof (ProductAttributeValue & VariantAttributeValue);
  variant: keyof (ProductAttributeValue & VariantAttributeValue);
} = {
  value: 'value',
  variant: 'variant',
};

const defaultMinOptionsForSelector = 7;
const defaultMaxItemsInRow = 3;

const getAttributeValue = (
  shopId: string,
  nameMarkersData: NameMarkersData,
  attribute?: ProductAttributeValue & VariantAttributeValue,
  nameType: NameSchemaType['id'] = 'ATTRIBUTE',
  mapping = defaultMapping
): string =>
  attribute?.[mapping.value]
    ? replaceNameMarkers(
        getNameByType((attribute?.[mapping.value] as SimpleAttributeValue)?.names, nameType, shopId),
        nameMarkersData
      )
    : replaceNameMarkers(
        getNameByType((attribute?.[mapping.variant] as Variant)?.names, nameType, shopId),
        nameMarkersData
      );

export const getPreparedValues = (
  values: (ProductAttributeValue | VariantAttributeValue)[] = [],
  attributeGroupId = -1,
  comparator: (value: ProductAttributeValue | VariantAttributeValue) => string | Attribute[] = () => ''
): (string | Attribute[])[] =>
  values.filter((attribute) => attribute.attributeGroup?.id === attributeGroupId).map(comparator);

export const getFlatAttributes = (attributes: Attribute[][]): Attribute[] => attributes.filter(Boolean).flat();

export const getByMathingValue = (names: Name[] | undefined, value: string) =>
  getNameByType(names, 'ATTRIBUTE_DESCRIPTION_HEADER') === value;

export const getMappedAttributes = (
  shopId: string,
  nameMarkersData: NameMarkersData,
  product?: Product,
  variantValues?: VariantAttributeValue[],
  nameSchemaTypeId: NameSchemaType['id'] = 'ATTRIBUTE',
  withIcons = false,
  separateMultipleValues = false
): Attribute[] =>
  product?.type?.productTypesAttributeGroups
    ?.filter((group) => (nameSchemaTypeId === 'MAIN_ATTRIBUTE' ? group?.isMainProperty : group?.isProperty))
    .flatMap((group) => {
      const variantAttribute = getPreparedValues(variantValues, group.attributeGroup?.id, (attribute) =>
        !attribute.isNone
          ? getAttributeValue(shopId, nameMarkersData, attribute, nameSchemaTypeId, {
              variant: 'targetVariant',
              value: 'attributes',
            })
          : ''
      );
      const productAttribute = getPreparedValues(product.attributes, group.attributeGroup?.id, (attribute) =>
        !attribute.isNone ? getAttributeValue(shopId, nameMarkersData, attribute, nameSchemaTypeId) : ''
      );
      const nestedVariantAttribute = getPreparedValues(
        variantValues,
        group.attributeGroup?.id,
        (attribute: VariantAttributeValue) =>
          attribute.targetVariant
            ? getMappedAttributes(
                shopId,
                nameMarkersData,
                attribute.targetVariant.product,
                attribute.targetVariant.variantsAttributesValues,
                nameSchemaTypeId,
                withIcons
              )
            : []
      ) as Attribute[][];
      const nestedProductAttribute = getPreparedValues(
        product.attributes,
        group.attributeGroup?.id,
        (attribute: ProductAttributeValue) =>
          attribute.variant
            ? getMappedAttributes(
                shopId,
                nameMarkersData,
                attribute.variant.product,
                attribute.variant.variantsAttributesValues,
                nameSchemaTypeId,
                withIcons
              )
            : []
      ) as Attribute[][];

      const singleValue = (variantAttribute.length ? variantAttribute : productAttribute) as string[];
      const finalValue = group?.hasMultipleValues
        ? getUniqueFlatArray([...(productAttribute as string[]), ...(variantAttribute as string[])])
        : singleValue;
      const finalAttributes = nestedVariantAttribute.length ? nestedVariantAttribute : nestedProductAttribute;

      if (finalValue.length > 1 && (finalAttributes.flat().length || separateMultipleValues)) {
        return finalValue.map((value, index) => {
          const variantMatchingValue = variantValues?.find((v) => getByMathingValue(v.attributes?.names, value));
          const productMatchingValue = product.attributes?.find((v) => getByMathingValue(v.value?.names, value));

          return {
            id: `${group.id}-${index}`,
            name: index ? `${group.attributeGroup?.name} #${index + 1}` : group.attributeGroup?.name || '',
            value: [value],
            icon: withIcons
              ? variantMatchingValue?.attributes?.iconFile ||
                productMatchingValue?.value?.iconFile ||
                group.attributeGroup?.icon
              : null,
            attributes: finalAttributes[index] || [],
          };
        });
      }

      return {
        id: `${group.id}`,
        name: group.attributeGroup?.name || '',
        value: finalValue,
        icon: withIcons ? group.attributeGroup?.icon : null,
        attributes: getFlatAttributes(finalAttributes),
      };
    })
    .filter(({ value }) => value.length) || [];

export const getMappedAdditionalAttributes = <T extends string>(
  intl: IntlShape,
  additionalAttributes: Record<T, MessageDescriptor>,
  data: Record<T, string[]>
): Attribute[] =>
  keysOf(additionalAttributes).map((key) => ({
    id: key,
    name: intl.formatMessage(additionalAttributes[key]),
    value: data[key],
    attributes: [],
  }));

export const getFallbackIcon = (product: Product): Picture[] =>
  product.type?.icon
    ? [
        {
          path: '',
          baseName: product.type.icon.basename,
          productName: product.name,
          extension: product.type.icon.extension,
          description: '',
        },
      ]
    : [];

export const getMappedMedia = (product: Product, variantMedia: Media[], names: Name[], shopId?: string): Picture[] => {
  const variantMediaGallery = variantMedia.filter(({ mediaTypeId }) => mediaTypeId === 'GALLERY');
  const productMediaGallery = product.media?.filter(({ mediaTypeId }) => mediaTypeId === 'GALLERY');
  const mediaSource = variantMediaGallery.length ? variantMediaGallery : productMediaGallery;

  return mediaSource?.length
    ? mediaSource.map((media) => ({
        path: '',
        baseName: media.file?.basename,
        productName: product.name,
        extension: media.file?.extension,
        width: media.file?.width,
        height: media.file?.height,
        description: `${getNameByType(names, 'MEDIA_ALT', shopId)} ${media.description || ''}`,
      }))
    : getFallbackIcon(product);
};

export const getVariantVideo = (product: Product, variantMedia: Media[]) =>
  ([...variantMedia, ...(product.media || [])].find(({ mediaTypeId }) => mediaTypeId === 'VIDEO') ||
    null) as Required<Media>;

export const isAttributeGroupMatching = (variantAttributeValue?: VariantAttributeValue, groupId?: number): boolean =>
  variantAttributeValue?.attributeGroup?.id === groupId;

export const getUniqAttributeValues = (attributeValues: VariantAttributeValue[]): VariantAttributeValue[] => {
  const uniqueValues: VariantAttributeValue[] = [];

  attributeValues.forEach((attributeValue) => {
    const isNoneValueInArray = !attributeValue?.isNone || uniqueValues.find((value) => value.isNone);
    const isSimpleValueInArray = uniqueValues.find((value) => value.attributes?.id === attributeValue.attributes?.id);
    const isVariantInArray = uniqueValues.find(
      (value) => value.targetVariant?.id === attributeValue?.targetVariant?.id
    );

    if (!isNoneValueInArray || !isSimpleValueInArray || !isVariantInArray) {
      uniqueValues.push(attributeValue);
    }
  });

  return uniqueValues;
};

export const getAttributeValuesFromGroup = (
  productVariants: Product['variants'],
  groupId?: AttributeGroup['id']
): VariantAttributeValue[] =>
  productVariants
    ?.flatMap((variant) => variant.variantsAttributesValues || [])
    .filter((attributeValue) => isAttributeGroupMatching(attributeValue, groupId)) || [];

export const getVariantsOptions = (
  product: Pick<Product, 'variantsAttributesGroups'>,
  availableShopsVariants: Product['variants'],
  variantId: number
): Record<string, string> => {
  const options: Record<string, string> = {};

  product.variantsAttributesGroups?.forEach((group) => {
    const selectedValue = getAttributeValuesFromGroup(availableShopsVariants, group.attributeGroup?.id).find(
      (attributeValue) => attributeValue.variantId === variantId
    );
    options[`${group.attributeGroup?.id}`] = `${
      selectedValue?.attributes?.id || selectedValue?.targetVariant?.id || (selectedValue?.isNone ? 'none-value' : '')
    }`;
  });

  return options;
};

export const getAttributeValues = (
  productVariants: Product['variants'],
  variantId: number,
  attributeGroupId?: number
): VariantAttributeValue[] =>
  productVariants
    ?.find((variant) => variant.id === variantId)
    ?.variantsAttributesValues?.filter((value) => value.attributeGroup?.id !== attributeGroupId) || [];

export const getVariantAttributeValues = (
  productVariants: Product['variants'],
  variantId: number,
  attributeValue: VariantAttributeValue,
  variantAttributeGroups: ProductAttributeGroup[] = []
): VariantAttributeValue[] | undefined =>
  productVariants
    ?.find((variant) => variant.id === variantId)
    ?.variantsAttributesValues?.filter(
      (currentAttributeValue) =>
        !!variantAttributeGroups.find((group) =>
          isAttributeGroupMatching(currentAttributeValue, group.attributeGroup?.id)
        )
    )
    .filter(
      (currentAttributeValue) =>
        getUniqAttributeValues(getAttributeValuesFromGroup(productVariants, currentAttributeValue.attributeGroup?.id))
          .length > 1
    )
    .filter(
      (currentAttributeValue) => !isAttributeGroupMatching(attributeValue, currentAttributeValue.attributeGroup?.id)
    ) || [];

export const getExactRelatedVariant = (
  productVariants: Product['variants'],
  variantId: number,
  attributeValue: VariantAttributeValue,
  currentVariantAttributeValues: VariantAttributeValue[] = [],
  onlyAvailableVariantsInSelector = false
): Variant | undefined => {
  const relevantVariants = productVariants?.filter(
    (variant) =>
      (!onlyAvailableVariantsInSelector || variant.availability) &&
      !variant.isEol &&
      [attributeValue, ...currentVariantAttributeValues].every((variantAttributeValue) =>
        checkIfAttributeValueMatch(
          variantAttributeValue,
          variant.variantsAttributesValues?.find(
            (value) => value.attributeGroup?.id === variantAttributeValue.attributeGroup?.id
          )
        )
      )
  );

  return relevantVariants?.find(({ id }) => id === variantId) || relevantVariants?.[0];
};

export const getPartiallyRelatedVariant = (
  variantSelectorAlgorithm: ShopConfig['variantSelectorAlgorithm'],
  productVariants: Product['variants'],
  attributeValue: VariantAttributeValue,
  currentVariantAttributeValues: VariantAttributeValue[] = [],
  onlyAvailableVariantsInSelector = false
): Variant | undefined => {
  const withPromotion = variantSelectorPromotionAlgorithms.includes(variantSelectorAlgorithm);
  const scoringMap: { variantId: number; score: number }[] =
    productVariants
      ?.filter(
        (variant) =>
          (!onlyAvailableVariantsInSelector || variant.availability) &&
          !variant.isEol &&
          checkIfAttributeValueMatch(
            attributeValue,
            variant.variantsAttributesValues?.find(
              (value) => value.attributeGroup?.id === attributeValue.attributeGroup?.id
            )
          )
      )
      .map((variant) => ({
        variantId: variant.id,
        score:
          currentVariantAttributeValues.filter((variantAttributeValue) =>
            checkIfAttributeValueMatch(
              variantAttributeValue,
              variant.variantsAttributesValues?.find(
                (value) => value.attributeGroup?.id === variantAttributeValue.attributeGroup?.id
              )
            )
          ).length * (withPromotion && variant.availability ? 2 : 1),
      })) || [];

  return productVariants?.find(({ id }) => id === scoringMap.sort((a, b) => b.score - a.score)[0]?.variantId);
};

export const getPriceImpact = (currentVariant?: Variant, newVariant?: Variant): PriceImpact | undefined => {
  const newVariantPrice = newVariant?.grossPrice;
  const currentVariantPrice = currentVariant?.grossPrice;
  return newVariantPrice
    ? {
        amount: (newVariantPrice || 0) - (currentVariantPrice || 0),
        currency: 'PLN',
        hasImpact: true,
      }
    : undefined;
};

export const getAttributesDescription = (
  ...props: [string, NameMarkersData, Product | undefined, VariantAttributeValue[] | undefined]
): { attributesHeaders: Attribute[]; attributesParagraphs: Attribute[] } => ({
  attributesHeaders: getMappedAttributes(...props, 'ATTRIBUTE_DESCRIPTION_HEADER', true, true),
  attributesParagraphs: getMappedAttributes(...props, 'ATTRIBUTE_DESCRIPTION', false, true),
});

export const getVariantsOptionGroups = (
  product: Pick<Product, 'variantsAttributesGroups'>,
  availableShopsVariants: Product['variants'],
  variantId: number,
  shopId: string,
  variantSelectorAlgorithm: ShopConfig['variantSelectorAlgorithm'],
  onlyAvailableVariantsInSelector: boolean
): OptionGroup[] =>
  product.variantsAttributesGroups
    ?.map(
      (group): OptionGroup => ({
        id: `${group.attributeGroup?.id}`,
        title: group.attributeGroup?.name || '',
        collapsedItemsMinCount:
          group.attributeGroup?.variantSelectorCollapsedItemsMinCount || defaultMinOptionsForSelector,
        maxItemsInRow: group.attributeGroup?.variantSelectorMaxItemsInRow || defaultMaxItemsInRow,
        options: getUniqAttributeValues(getAttributeValuesFromGroup(availableShopsVariants, group.attributeGroup?.id))
          .sort((a, b) => (a?.selectorWeight || 0) - (b?.selectorWeight || 0))
          .map((attributeValue) => {
            const currentAttributeValues = getVariantAttributeValues(
              availableShopsVariants,
              variantId,
              attributeValue,
              product?.variantsAttributesGroups
            );
            const relatedVariant =
              getExactRelatedVariant(
                availableShopsVariants,
                variantId,
                attributeValue,
                currentAttributeValues,
                onlyAvailableVariantsInSelector
              ) ||
              getPartiallyRelatedVariant(
                variantSelectorAlgorithm,
                availableShopsVariants,
                attributeValue,
                currentAttributeValues,
                onlyAvailableVariantsInSelector
              );
            const currentVariant = confidentFind(availableShopsVariants as Variant[], (v) => v.id === variantId);
            const accessibleStatus = relatedVariant?.availability ? 'active' : 'soon';
            const status: OptionStatus = relatedVariant?.id ? accessibleStatus : 'inactive';

            const result: Optional<Option, 'variantId' | 'urlSlug'> = {
              id: `${
                attributeValue?.attributes?.id ||
                attributeValue?.targetVariant?.id ||
                (attributeValue?.isNone ? 'none-value' : '')
              }`,
              name: getAttributeValue(shopId, {}, attributeValue, 'VARIANT_SELECTOR', {
                value: 'attributes',
                variant: 'targetVariant',
              }),
              urlSlug: relatedVariant?.urlSlug,
              priceImpact: getPriceImpact(currentVariant, relatedVariant),
              status,
              variantId: relatedVariant?.id,
              variantAttributeValue: attributeValue,
            };

            return result as Option;
          })
          .filter(({ variantId: id }) => !!id),
      })
    )
    .filter(({ options }) => options.length > (onlyAvailableVariantsInSelector ? 0 : 1)) || [];

export const getMappedPackageContents = (packageContents: VariantPackageContent[]): PackageContent[] =>
  packageContents.map(({ packageContent }) => packageContent);

export const getSearchConstantFilters = (hits: number = 0, filters: Filter[] = []): QueryFilter[] => {
  const constantFilters: QueryFilter[] = [];
  if (hits) {
    filters.forEach(({ key, values }) => {
      if (['manufacturer', 'product_family', 'product_series'].includes(key)) {
        const filteredValues = (values as FilterValue[]).filter(({ name, count }) => name && count === hits);
        const availableValues = (values as FilterValue[]).filter(({ name, count }) => name && count);
        if (filteredValues.length === availableValues.length) {
          constantFilters.push({ key, values: map(filteredValues, 'name') });
        }
      }
    });
  }

  return constantFilters;
};

export const getProductConstantFilters = (
  product: Omit<ProductCategoryLevels, 'manufacturer' | 'family'>,
  hits: number = 0,
  filters: Filter[] = []
): QueryFilter[] => [
  { key: 'product', values: [product.name] },
  { key: 'product_type', values: [product.type.name] },
  ...getSearchConstantFilters(hits, filters),
];
